import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Haptics } from '../../utils/hapticHelper';

const PullToRefresh = ({ onRefresh, threshold = 80, maxPullDistance = 150 }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [canPull, setCanPull] = useState(false);
  
  const startY = useRef(0);
  const currentY = useRef(0);
  const containerRef = useRef(null);

  // Check if we're at the top of the page
  const checkScrollPosition = useCallback(() => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    setCanPull(scrollTop === 0);
  }, []);

  useEffect(() => {
    checkScrollPosition();
    window.addEventListener('scroll', checkScrollPosition, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  const handleTouchStart = useCallback((e) => {
    if (!canPull || isRefreshing) return;
    
    startY.current = e.touches[0].clientY;
    setIsPulling(true);
  }, [canPull, isRefreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || isRefreshing) return;
    
    currentY.current = e.touches[0].clientY;
    const distance = currentY.current - startY.current;
    
    if (distance > 0) {
      // Prevent default scrolling when pulling down
      if (distance > 10) {
        e.preventDefault();
      }
      
      // Apply resistance - the further you pull, the harder it gets
      const resistance = Math.min(distance / 2.5, maxPullDistance);
      setPullDistance(resistance);
      
      // Light haptic feedback when reaching threshold
      if (resistance >= threshold && pullDistance < threshold) {
        Haptics.custom(15); // Quick tap feedback
      }
    }
  }, [isPulling, isRefreshing, maxPullDistance]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || isRefreshing) return;
    
    setIsPulling(false);
    
    if (pullDistance >= threshold) {
      // Haptic feedback on release
      Haptics.pullToRefresh();
      setIsRefreshing(true);
      
      try {
        // Trigger refresh callback
        if (onRefresh) {
          await onRefresh();
        } else {
          // Default behavior: reload the page
          await new Promise(resolve => setTimeout(resolve, 1000));
          window.location.reload();
        }
      } catch (error) {
        console.error('Refresh error:', error);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      // Reset if not pulled enough
      setPullDistance(0);
    }
  }, [isPulling, isRefreshing, pullDistance, threshold, onRefresh]);

  // Add touch event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const progressPercentage = Math.min((pullDistance / threshold) * 100, 100);
  const isReady = pullDistance >= threshold;

  return (
    <div 
      ref={containerRef}
      className={`pull-to-refresh-container ${isPulling ? 'pulling' : ''} ${isRefreshing ? 'refreshing' : ''}`}
      style={{
        '--pull-distance': `${pullDistance}px`,
        '--progress': `${progressPercentage}%`
      }}
    >
      <div className="pull-to-refresh-indicator">
        <div className="refresh-content">
          {isRefreshing ? (
            <>
              <div className="refresh-spinner">
                <div className="spinner-circle"></div>
              </div>
              <span className="refresh-text">Refreshing...</span>
            </>
          ) : isReady ? (
            <>
              <div className="refresh-icon ready">
                <i className='bx bx-chevron-up'></i>
              </div>
              <span className="refresh-text">Release to refresh</span>
            </>
          ) : pullDistance > 0 ? (
            <>
              <div className="refresh-icon pulling" style={{ transform: `rotate(${progressPercentage * 1.8}deg)` }}>
                <i className='bx bx-chevron-down'></i>
              </div>
              <span className="refresh-text">Pull to refresh</span>
            </>
          ) : null}
        </div>
        
      </div>
    </div>
  );
};

export default PullToRefresh;

